import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { Button, Grid, Input, Paper, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import { useUploadFile } from '../../../sic-lib/api/hooks/useMyFile';
import { useAppDispatch } from '../../../store/hooks';
import { addSelectedMedia } from '../../../store/media';
import AxiosErrorAlert from '../../Shared/Alerts/AxiosErrorAlert';
import ButtonLoading from '../../Shared/ButtonLoading';
import UserMediaPreviewItem from './UserMediaPreviewItem';

interface Props {
    contactId: number;
    onCloseHandler: () => void;
}

export default function UploadMediaTab({ contactId, onCloseHandler }: Props) {
    const { classes, cx } = useStyles();
    const dispatch = useAppDispatch();

    const [files, setFiles] = React.useState<File[]>([]);
    const [isDragging, setIsDragging] = React.useState<boolean>(false);

    const {
        mutateAsync: uploadMutation,
        isLoading: isUploadLoading,
        isError: isUploadError,
        isSuccess: isUploadSuccess,
        error: uploadError,
    } = useUploadFile();

    const onDrop = async (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        const updatedFiles = await checkAndReplaceFiles(files, event.dataTransfer.files);
        setFiles(updatedFiles);
        setIsDragging(false);
    };

    const onDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const onDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
        setIsDragging(true);
    };

    const onDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        setIsDragging(false);
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const target = e.target as HTMLInputElement & {
            files: FileList;
        };
        const updatedFiles = await checkAndReplaceFiles(files, target.files);
        setFiles(updatedFiles);
    };

    const handleSubmit = async () => {
        try {
            console.log('Files:', files);
            const formData = await handleFormData(files);
            const uploadedImage = await uploadMutation(formData);

            dispatch(addSelectedMedia({ selectedMedia: uploadedImage }));
            setFiles([]);
            /* dispatch(
                addMediaToContactComposeMessage({
                    contactId,
                    media: uploadedImage.map(image => image.url) || undefined,
                }),
            );
            setFiles([]);
            onCloseHandler(); */
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const handleClear = () => {
        setFiles([]);
    };

    React.useEffect(() => {
        return () => {
            setFiles([]);
        };
    }, []);
    return (
        <>
            <Typography variant="h6" mb={2}>
                File Upload
            </Typography>
            <Grid
                container
                flex={1}
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
            >
                <Typography component="label" htmlFor="file-upload" variant="body1">
                    <Paper
                        className={cx(classes.paperContainer, isDragging && classes.paperDragging)}
                        elevation={isDragging ? 3 : 1}
                    >
                        <Grid
                            container
                            height="100%"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            component="div"
                            onDrop={onDrop}
                            onDragOver={onDragOver}
                            onDragEnter={onDragEnter}
                            onDragLeave={onDragLeave}
                        >
                            <CloudUploadOutlinedIcon
                                sx={{ fontSize: 80 }}
                                className={classes.Icon}
                            />
                            <Typography mb={2} className={classes.Icon}>
                                Drag and drop files here or
                            </Typography>
                            <Input
                                name="file-upload"
                                id="file-upload"
                                className={classes.input}
                                type="file"
                                inputProps={{ multiple: true, accept: '*' }}
                                onChange={handleFileChange}
                            />
                            <Button variant="contained" color="primary" component="span">
                                Upload Files
                            </Button>
                        </Grid>
                    </Paper>
                </Typography>
            </Grid>
            {files.length > 0 && <UserMediaPreviewItem files={files} />}
            {files.length > 0 && (
                <Grid
                    container
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Button variant="contained" color="primary" onClick={handleClear}>
                        Clear All Files
                    </Button>
                    <ButtonLoading
                        variant="contained"
                        color="primary"
                        isLoading={isUploadLoading}
                        isSuccess={isUploadSuccess}
                        successLabel="Files Uploaded"
                        isError={isUploadError}
                        onClick={handleSubmit}
                    >
                        Upload {files.length > 0 && `${files.length}`}{' '}
                        {files.length > 1 ? 'Files' : 'File'} to MyFiles
                    </ButtonLoading>
                </Grid>
            )}
            {isUploadError && <AxiosErrorAlert error={uploadError} />}
        </>
    );
}

const handleFormData = async (assets: File[]) => {
    const formData = new FormData();
    assets.forEach((asset, index) => {
        formData.append('file', asset);
    });
    return formData;
};

const checkAndReplaceFiles = async (stateFile: File[], uploadedFile: FileList): Promise<File[]> => {
    const selectedFiles = Array.from(uploadedFile);
    const updatedFiles = selectedFiles.reduce(
        (acc, file) => {
            const existingFileIndex = stateFile.findIndex(f => f.name === file.name);
            if (existingFileIndex !== -1) {
                acc[existingFileIndex] = file;
            } else {
                acc.push(file);
            }
            return acc;
        },
        [...stateFile],
    );
    return updatedFiles;
};

const useStyles = makeStyles()((theme: Theme) => ({
    paperContainer: {
        height: 400,
        width: 400,
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: theme.palette.addButton.light,
            border: '1px dashed black',
        },
    },
    paperDragging: {
        backgroundColor: theme.palette.addButton.light,
        border: '1px dashed black',
    },
    input: {
        display: 'none',
    },
    media: {
        maxWidth: '50px',
        maxHeight: '50px',
    },
    Icon: {
        color: theme.palette.noteStatus.light,
    },
}));
